import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Package, Box, Truck, Building2, AlertTriangle, ArrowRight,
  Warehouse, BookmarkCheck, MapPin, CheckCircle2, Wrench, AlertOctagon,
  Hourglass, Inbox, Send, Plug,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { TICKET_STATUS_COLORS } from "@/components/TicketStatusBadge";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid,
} from "recharts";

const PALETTE = ["#2563eb", "#0891b2", "#16a34a", "#f59e0b", "#dc2626", "#7c3aed", "#475569", "#db2777", "#0ea5e9", "#84cc16"];

const TONES = {
  sky:    { bg: "bg-sky-50",     icon: "bg-sky-100 text-sky-700",         ring: "ring-sky-100/80" },
  blue:   { bg: "bg-blue-50",    icon: "bg-blue-100 text-blue-700",       ring: "ring-blue-100/80" },
  amber:  { bg: "bg-amber-50",   icon: "bg-amber-100 text-amber-700",     ring: "ring-amber-100/80" },
  indigo: { bg: "bg-indigo-50",  icon: "bg-indigo-100 text-indigo-700",   ring: "ring-indigo-100/80" },
  cyan:   { bg: "bg-cyan-50",    icon: "bg-cyan-100 text-cyan-700",       ring: "ring-cyan-100/80" },
  emerald:{ bg: "bg-emerald-50", icon: "bg-emerald-100 text-emerald-700", ring: "ring-emerald-100/80" },
  rose:   { bg: "bg-rose-50",    icon: "bg-rose-100 text-rose-700",       ring: "ring-rose-100/80" },
  violet: { bg: "bg-violet-50",  icon: "bg-violet-100 text-violet-700",   ring: "ring-violet-100/80" },
  orange: { bg: "bg-orange-50",  icon: "bg-orange-100 text-orange-700",   ring: "ring-orange-100/80" },
  slate:  { bg: "bg-slate-50",   icon: "bg-slate-200 text-slate-700",     ring: "ring-slate-200/80" },
};

function StatCard({ label, value, icon: Icon, tone = "slate", alert }) {
  const t = TONES[tone] || TONES.slate;
  return (
    <Card className="card-lift border-slate-200 bg-white">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
          <div className={cn("flex h-8 w-8 items-center justify-center rounded-lg", t.icon)}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <p className={cn("mt-3 text-3xl font-bold tracking-tight", alert && value > 0 ? "text-rose-600" : "text-slate-900")}>{value}</p>
      </CardContent>
    </Card>
  );
}

function StatGrid({ title, icon: Icon, cards }) {
  return (
    <section className="space-y-3">
      <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
        <Icon className="h-4 w-4" /> {title}
      </h3>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => <StatCard key={c.label} {...c} />)}
      </div>
    </section>
  );
}

function Empty({ label }) {
  return <div className="flex h-[250px] items-center justify-center text-sm text-muted-foreground">{label}</div>;
}

function ChartsGrid({ charts }) {
  return (
    <section className="grid gap-4 lg:grid-cols-2">
      <Card className="border-slate-200 card-lift">
        <CardHeader><CardTitle className="text-base">Shipments per Month</CardTitle></CardHeader>
        <CardContent>
          {(charts.shipments_per_month || []).every((d) => d.count === 0) ? <Empty label="No shipments yet" /> : (
            <ResponsiveContainer width="100%" height={250} minWidth={120} minHeight={120} initialDimension={{ width: 400, height: 250 }}>
              <BarChart data={charts.shipments_per_month} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "rgba(37,99,235,0.06)" }} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Bar dataKey="count" name="Shipments" fill="url(#barGrad)" radius={[6, 6, 0, 0]} />
                <defs>
                  <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
      <Card className="border-slate-200 card-lift" data-testid="ticket-status-overview-card">
        <CardHeader><CardTitle className="text-base">Ticket Status Overview</CardTitle></CardHeader>
        <CardContent>
          {(() => {
            const ticketData = (charts.ticket_status || []).filter((d) => d.value > 0);
            const total = ticketData.reduce((s, d) => s + d.value, 0);
            if (ticketData.length === 0) return <Empty label="No tickets yet" />;
            return (
              <>
                <div className="relative mx-auto aspect-square w-full max-w-[220px]">
                  <ResponsiveContainer width="100%" height="100%" minWidth={120} minHeight={120} initialDimension={{ width: 220, height: 220 }}>
                    <PieChart>
                      <Pie data={ticketData} dataKey="value" nameKey="name" innerRadius="62%" outerRadius="90%" paddingAngle={2} stroke="none" isAnimationActive={false}>
                        {ticketData.map((d) => <Cell key={d.name} fill={TICKET_STATUS_COLORS[d.name] || "#94a3b8"} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <p className="text-3xl font-bold leading-none tracking-tight" data-testid="dashboard-ticket-total">{total}</p>
                    <p className="mt-1 text-xs text-slate-500">Tickets</p>
                  </div>
                </div>
                <div className="mt-3 space-y-1.5">
                  {ticketData.map((d) => {
                    const pct = total ? Math.round((d.value / total) * 100) : 0;
                    return (
                      <Link
                        key={d.name}
                        to="/tickets"
                        className="flex w-full items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs transition hover:bg-slate-50"
                        data-testid={`dashboard-ticket-status-${d.name.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        <span className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full" style={{ background: TICKET_STATUS_COLORS[d.name] || "#94a3b8" }} />
                          <span className="font-medium text-slate-700">{d.name}</span>
                        </span>
                        <span className="text-slate-500">{d.value} <span className="text-slate-400">({pct}%)</span></span>
                      </Link>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </CardContent>
      </Card>
      <Card className="border-slate-200 card-lift" data-testid="product-status-breakdown-card">
        <CardHeader><CardTitle className="text-base">Product Status Breakdown</CardTitle></CardHeader>
        <CardContent>
          {(() => {
            const data = (charts.product_status || []).filter((d) => d.value > 0);
            if (data.length === 0) return <Empty label="No data yet" />;
            return (
              <div className="flex items-center gap-4">
                <div className="aspect-square w-full max-w-[180px] shrink-0">
                  <ResponsiveContainer width="100%" height="100%" minWidth={120} minHeight={120} initialDimension={{ width: 180, height: 180 }}>
                    <PieChart>
                      <Pie data={data} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="90%" paddingAngle={2} stroke="none" isAnimationActive={false}>
                        {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="flex-1 space-y-1 text-xs" data-testid="product-status-legend">
                  {data.map((d, i) => (
                    <li key={d.name} className="flex items-center justify-between gap-2">
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                        <span className="truncate text-slate-700">{d.name}</span>
                      </span>
                      <span className="font-medium text-slate-900">{d.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}
        </CardContent>
      </Card>
      <Card className="border-slate-200 card-lift" data-testid="crate-fleet-status-card">
        <CardHeader><CardTitle className="text-base">Crate Fleet Status</CardTitle></CardHeader>
        <CardContent>
          {(() => {
            const data = (charts.crate_fleet || []).filter((d) => d.value > 0);
            if (data.length === 0) return <Empty label="No data yet" />;
            return (
              <div className="flex items-center gap-4">
                <div className="aspect-square w-full max-w-[180px] shrink-0">
                  <ResponsiveContainer width="100%" height="100%" minWidth={120} minHeight={120} initialDimension={{ width: 180, height: 180 }}>
                    <PieChart>
                      <Pie data={data} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="90%" paddingAngle={2} stroke="none" isAnimationActive={false}>
                        {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="flex-1 space-y-1 text-xs" data-testid="crate-fleet-legend">
                  {data.map((d, i) => (
                    <li key={d.name} className="flex items-center justify-between gap-2">
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                        <span className="truncate text-slate-700">{d.name}</span>
                      </span>
                      <span className="font-medium text-slate-900">{d.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}
        </CardContent>
      </Card>
    </section>
  );
}

function WelcomeBanner({ subtitle, role, clientsCount }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:p-7">
      <div className="flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-blue-700 text-white shadow-sm">
          <Warehouse className="h-6 w-6" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{role === "client_user" ? "Client View" : "Operations Dashboard"}</p>
          <h2 className="mt-0.5 text-2xl font-bold tracking-tight text-slate-900">{role === "client_user" ? "My Inventory" : "Welcome back"}</h2>
          <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>
        </div>
      </div>
      {typeof clientsCount === "number" && (
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-700">
          <Building2 className="h-4 w-4 text-slate-500" />
          <span className="font-semibold text-slate-900">{clientsCount}</span> active organization{clientsCount === 1 ? "" : "s"}
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    function fetchStats() {
      api.get("/dashboard/stats")
        .then((r) => { if (alive) setData(r.data); })
        .finally(() => { if (alive) setLoading(false); });
    }
    fetchStats();
    // Re-fetch when the tab regains focus so the dashboard reflects deletes/edits
    // made elsewhere (other tabs, or after navigating back from /clients).
    function onFocus() { fetchStats(); }
    function onVisible() { if (document.visibilityState === "visible") fetchStats(); }
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  if (loading) return <p className="text-muted-foreground">Loading dashboard…</p>;
  if (!data) return <p className="text-muted-foreground">No data.</p>;

  if (data.role === "client_user") {
    const c = data.cards;
    return (
      <div className="space-y-8">
        <WelcomeBanner role="client_user" subtitle={`Stored at Dolphin Warehouse — Dallas, TX · ${user?.full_name || user?.email}`} />
        <StatGrid title="My Products" icon={Package} cards={[
          { label: "In Warehouse", value: c.in_warehouse, icon: Warehouse, tone: "sky" },
          { label: "Reserved", value: c.reserved, icon: BookmarkCheck, tone: "amber" },
          { label: "In Transit", value: c.in_transit, icon: Send, tone: "indigo" },
          { label: "Delivered (Not Installed)", value: c.delivered, icon: MapPin, tone: "cyan" },
          { label: "Installed", value: c.installed, icon: Plug, tone: "emerald" },
          { label: "Damaged / Missing", value: c.problem, icon: AlertOctagon, tone: "rose", alert: true },
        ]} />
        <StatGrid title="Crates & Shipments" icon={Box} cards={[
          { label: "Active Shipments", value: c.shipments_active, icon: Truck, tone: "blue" },
          { label: "Crates Pending Return", value: c.crates_pending_return, icon: Hourglass, tone: "orange" },
        ]} />
        <section className="space-y-3">
          <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
            <Package className="h-4 w-4" /> Current Inventory
          </h3>
          <Card className="border-slate-200">
            <CardContent className="p-0">
              {data.inventory && data.inventory.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50/50 text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="px-4 py-3 font-medium">Serial Number</th>
                      <th className="px-4 py-3 font-medium">Type</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Location</th>
                      <th className="px-4 py-3 font-medium">Last Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.inventory.map((p) => (
                      <tr key={p.id} className="border-b last:border-0 transition-colors hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-900">{p.serial_number}</td>
                        <td className="px-4 py-3 text-slate-700">{p.product_type}</td>
                        <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                        <td className="px-4 py-3 text-slate-700">{p.current_location}</td>
                        <td className="px-4 py-3 text-slate-500">{new Date(p.updated_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="p-10 text-center text-muted-foreground">No products in inventory yet.</p>}
            </CardContent>
          </Card>
        </section>
        <ChartsGrid charts={data.charts || {}} />
      </div>
    );
  }

  // Staff dashboard
  const charts = data.charts || {};
  return (
    <div className="space-y-8">
      <WelcomeBanner role="staff" subtitle="Real-time view of products, crates, and shipments at Dolphin Warehouse." clientsCount={data.clients_active} />

      <StatGrid title="Products" icon={Package} cards={[
        { label: "In Warehouse", value: data.products.in_warehouse, icon: Warehouse, tone: "sky" },
        { label: "Reserved", value: data.products.reserved, icon: BookmarkCheck, tone: "amber" },
        { label: "Shipped (In Transit)", value: data.products.shipped, icon: Send, tone: "indigo" },
        { label: "Delivered", value: data.products.delivered, icon: MapPin, tone: "cyan" },
        { label: "Installed", value: data.products.installed, icon: Plug, tone: "emerald" },
        { label: "Damaged / Missing", value: data.products.problem, icon: AlertOctagon, tone: "rose", alert: true },
      ]} />

      <StatGrid title="Wooden Crates" icon={Box} cards={[
        { label: "In Warehouse with DSK", value: data.crates.with_dsk, icon: Box, tone: "sky" },
        { label: "Empty in Warehouse", value: data.crates.empty, icon: Inbox, tone: "slate" },
        { label: "In Transit", value: data.crates.in_transit, icon: Truck, tone: "indigo" },
        { label: "Waiting for Return", value: data.crates.waiting, icon: Hourglass, tone: "orange", alert: true },
      ]} />

      <StatGrid title="Shipments" icon={Truck} cards={[
        { label: "Active Shipments", value: data.shipments.active, icon: Truck, tone: "blue" },
        { label: "Waiting for Box Return", value: data.shipments.waiting_box, icon: Hourglass, tone: "orange" },
        { label: "Exceptions / Issues", value: data.shipments.issues, icon: AlertTriangle, tone: "rose", alert: true },
      ]} />

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
            <Truck className="h-4 w-4" /> Recent Shipments
          </h3>
          <Link to="/shipments" className="inline-flex items-center gap-1 text-sm font-medium text-sky-700 hover:text-sky-900">
            View all <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {data.recent_shipments && data.recent_shipments.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50/50 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3 font-medium">Shipment</th>
                    <th className="px-4 py-3 font-medium">Organization</th>
                    <th className="px-4 py-3 font-medium">Destination</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_shipments.map((s) => (
                    <tr key={s.id} className="border-b last:border-0 transition-colors hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium">
                        <Link to={`/shipments/${s.id}`} className="text-sky-700 hover:text-sky-900 hover:underline">{s.shipment_code}</Link>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{s.client_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-700">{s.store_name} · {s.city}, {s.state}</td>
                      <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                      <td className="px-4 py-3 text-slate-500">{new Date(s.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="flex flex-col items-center gap-2 p-10 text-center text-muted-foreground">
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                <p>No shipments yet. Create one to get started.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      <ChartsGrid charts={charts} />
    </div>
  );
}
